// src/onboarding/OnboardingWelcome.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: first onboarding scene — eyebrow + heading + experience slider
// + UK-postcode location input. Persists to tend:gardenSpaces, tend:experience,
// tend:location, tend:locationLatLon on continue.

import { useState, useEffect } from 'react';
import { Sprout, Check, MapPin } from 'lucide-react';
import OnbFrame from './OnbFrame.jsx';
import { ONBOARD_EXPERIENCE } from './constants.js';
import {
  isValidUkPostcode, outwardCode,
  requestGeolocation, reverseGeocode,
} from '../lib/location.js';
import {
  persistOnboardField, persistOnboardArray, persistOnboardJSON,
} from '../lib/storage.js';

export default function OnboardingWelcome({data, setData, onContinue, step = 0, total = 5}) {
  const [locStatus, setLocStatus] = useState("idle"); // "idle" | "detecting" | "valid" | "error"
  const [locError, setLocError] = useState("");
  const [locLatLon, setLocLatLon] = useState(null);

  // D-09..D-13 — experience slider. Map stored string to slider index.
  const expToIndex = { beginner: 0, some: 1, experienced: 2 };
  const indexToExp = ["beginner", "some", "experienced"];
  const initialIdx = expToIndex[data.experience] ?? 0;
  const [expIndex, setExpIndex] = useState(initialIdx);
  // Keep local slider index in sync with parent data if it changes (Back button from Plan 06).
  useEffect(() => {
    const idx = expToIndex[data.experience];
    if (typeof idx === "number" && idx !== expIndex) setExpIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.experience]);
  const handleExpChange = (e) => {
    const idx = Number(e.target.value);
    setExpIndex(idx);
    setData(prev => ({ ...prev, experience: indexToExp[idx] }));
  };

  // Re-derive validity whenever the user types
  useEffect(() => {
    const v = (data.location || "").trim();
    if (!v) { setLocStatus("idle"); setLocError(""); return; }
    if (isValidUkPostcode(v)) { setLocStatus("valid"); setLocError(""); }
    else { setLocStatus("error"); setLocError("Enter a UK postcode or outward code (e.g. SW1A)."); }
  }, [data.location]);

  const handleUseMyLocation = async () => {
    setLocStatus("detecting");
    setLocError("");
    try {
      const {lat, lon} = await requestGeolocation();
      const {outcode} = await reverseGeocode(lat, lon);
      setData(prev => ({...prev, location: outcode}));
      setLocLatLon({lat, lon});
      setLocStatus("valid");
    } catch (err) {
      setLocLatLon(null);
      const code = err && err.code;
      const message =
        code === "INSECURE_CONTEXT"     ? "Location needs a secure (https) connection. Enter a postcode manually." :
        code === "PERMISSION_DENIED"    ? "Location permission denied. Enter a postcode manually." :
        code === "TIMEOUT"              ? "Location detection timed out. Enter a postcode manually." :
        code === "UNAVAILABLE"          ? "Location isn't available on this device. Enter a postcode manually." :
        code === "POSITION_UNAVAILABLE" ? "Couldn't pinpoint your location right now. Enter a postcode manually." :
        code === "NO_MATCH"             ? "Couldn't find a UK postcode near you. Enter a postcode manually." :
        code === "NETWORK"              ? "Couldn't reach the location service. Enter a postcode manually." :
                                          "Couldn't detect your location. Enter a postcode manually.";
      setLocError(message);
      setLocStatus((data.location || "").trim() ? (isValidUkPostcode(data.location) ? "valid" : "error") : "idle");
    }
  };

  const handleContinue = () => {
    persistOnboardArray('tend:gardenSpaces', data.gardenSpaces);
    persistOnboardField('tend:experience', data.experience);
    const loc = (data.location || "").trim();
    if (loc && isValidUkPostcode(loc)) {
      persistOnboardField('tend:location', outwardCode(loc));
      persistOnboardJSON('tend:locationLatLon', locLatLon);
    }
    // If invalid or blank: do NOT overwrite tend:location; preserve any prior value.
    onContinue();
  };
  return (
    <OnbFrame step={step} total={total} ground="dawn" butterflies>
      <div className="onb-content">
        <div style={{ height: 36 }}/>

        {/* Wordmark — same Sprout icon + Tend word as before */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
          <Sprout size={20} style={{ color: "var(--c-heading)" }}/>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.375rem', fontWeight: 600, color: "var(--c-heading)" }}>Tend</span>
        </div>

        {/* Eyebrow MUST NOT contain "welcome" — singular getByText(/welcome/i) */}
        {/* at src/App.test.jsx:1579 would throw on multiple matches. */}
        <p className="onb-eyebrow">A quiet start</p>
        <h1 className="onb-heading">
          Welcome to <em>Tend</em>.
        </h1>
        <p className="onb-sub">
          Gardening is full of "should I do this now?" moments — feed the roses, prune the lavender, mulch the beds. Tend answers them, month by month, for the plants you actually grow.
        </p>
        <p className="onb-margin-note">Two quick questions so the advice fits your garden, not someone else's.</p>

        {/* D-09..D-13 — Experience slider block */}
        <div style={{ marginTop: 22 }}>
          <p className="onb-field-label">How much gardening experience do you have?</p>

          {/* D-11 — crossfading SVG illustrations (220×220) */}
          <div className="onb-experience-illus" aria-hidden="true">
            {/* Beginner — seedling with open sunflower head (Phase 31.3 Gap 3) */}
            <svg viewBox="0 0 220 220" className={expIndex === 0 ? "is-active" : ""}>
              <ellipse cx="110" cy="180" rx="80" ry="18" fill="#7a5a3d" opacity="0.35"/>
              <rect x="30" y="170" width="160" height="30" fill="#7a5a3d" opacity="0.55"/>
              <path d="M 110 170 Q 108 140 112 110 Q 114 95 110 72" stroke="#5a7a4a" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <path d="M 110 120 Q 92 108 82 118 Q 92 128 110 124 Z" fill="#6b8a55"/>
              <path d="M 110 100 Q 128 90 138 102 Q 128 112 110 106 Z" fill="#5a7a4a"/>
              {/* 12 petals around (110,72), alternating brand sunflower hues */}
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(0 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(30 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(60 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(90 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(120 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(150 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(180 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(210 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(240 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(270 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#f0c840" transform="rotate(300 110 72)"/>
              <ellipse cx="110" cy="56" rx="4" ry="11" fill="#e8a547" transform="rotate(330 110 72)"/>
              {/* dark centre disc + amber rim */}
              <circle cx="110" cy="72" r="8" fill="#7a5a3d"/>
              <circle cx="110" cy="72" r="8" fill="none" stroke="#c99420" strokeWidth="1"/>
              <circle cx="108" cy="70" r="1.5" fill="#c99420"/>
            </svg>
            {/* Some — potted hero with flower head + receding background blooms (Phase 31.3 Gap 3) */}
            <svg viewBox="0 0 220 220" className={expIndex === 1 ? "is-active" : ""}>
              {/* Background bloom silhouettes — opacity 0.45, frame but don't crowd the focal potted plant */}
              <g opacity="0.45">
                <path d="M 20 155 Q 18 130 22 108" stroke="#6b8a55" strokeWidth="1.5" fill="none"/>
                <circle cx="22" cy="105" r="4" fill="#a78bca"/>
                <circle cx="18" cy="110" r="3" fill="#a78bca"/>
                <circle cx="26" cy="112" r="3" fill="#c96878"/>
                <ellipse cx="196" cy="150" rx="22" ry="18" fill="#78965b" opacity="0.6"/>
                <circle cx="192" cy="138" r="4" fill="#d98b5f"/>
                <circle cx="202" cy="142" r="3" fill="#e8a547"/>
                <path d="M 160 155 Q 158 128 162 110" stroke="#9cb873" strokeWidth="1" fill="none"/>
                <circle cx="162" cy="108" r="3" fill="#e8a547"/>
              </g>
              {/* Focal potted plant */}
              <path d="M 70 150 L 150 150 L 140 200 L 80 200 Z" fill="#b8845a"/>
              <ellipse cx="110" cy="150" rx="40" ry="6" fill="#6b4a2b" opacity="0.5"/>
              <path d="M 110 150 Q 106 110 112 70 Q 116 50 110 35" stroke="#5a7a4a" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <ellipse cx="96" cy="90" rx="14" ry="9" fill="#6b8a55"/>
              <ellipse cx="124" cy="70" rx="14" ry="9" fill="#5a7a4a"/>
              {/* 6-petal mid-flower on top of the stalk, around (110,35) */}
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#d98b5f" transform="rotate(0 110 35)"/>
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#e8a547" transform="rotate(60 110 35)"/>
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#d98b5f" transform="rotate(120 110 35)"/>
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#e8a547" transform="rotate(180 110 35)"/>
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#d98b5f" transform="rotate(240 110 35)"/>
              <ellipse cx="110" cy="25" rx="3" ry="7" fill="#e8a547" transform="rotate(300 110 35)"/>
              <circle cx="110" cy="35" r="4" fill="#7a5a3d"/>
              {/* lower-side lilac bud for asymmetry — recoloured from raspberry to lilac to tie to bg cluster */}
              <circle cx="98" cy="60" r="4" fill="#a78bca"/>
            </svg>
            {/* Experienced — mature border with topiary standards + varied foreground flowers (Phase 31.3 Gap 3) */}
            <svg viewBox="0 0 220 220" className={expIndex === 2 ? "is-active" : ""}>
              {/* Soil + lawn bands anchor the scene */}
              <rect x="0" y="180" width="220" height="40" fill="#7a5a3d" opacity="0.35"/>
              <rect x="0" y="160" width="220" height="25" fill="#9cb873" opacity="0.45"/>
              {/* Topiary tree 1 — left-centre, bay-on-stem */}
              <rect x="44" y="110" width="3" height="50" fill="#8a6a4a"/>
              <ellipse cx="45.5" cy="105" rx="22" ry="20" fill="#5a7a4a"/>
              <ellipse cx="40" cy="100" rx="6" ry="4" fill="#6b8a55" opacity="0.6"/>
              {/* Topiary tree 2 — centre-right, larger hero topiary */}
              <rect x="143" y="100" width="4" height="60" fill="#8a6a4a"/>
              <ellipse cx="145" cy="92" rx="26" ry="24" fill="#6b8a55"/>
              <ellipse cx="139" cy="85" rx="7" ry="5" fill="#78965b" opacity="0.6"/>
              {/* Topiary tree 3 — far-right, smaller / more distant */}
              <rect x="196" y="118" width="3" height="42" fill="#8a6a4a"/>
              <ellipse cx="197.5" cy="114" rx="16" ry="14" fill="#5a7a4a"/>
              {/* Foreground flower band — VARIED sizes r=3/5/6/7/8/9/10 across all four brand hues */}
              {/* Hero cluster bottom-left */}
              <circle cx="22" cy="165" r="10" fill="#c96878"/>
              <circle cx="36" cy="172" r="6" fill="#e8a547"/>
              <circle cx="12" cy="170" r="4" fill="#d98b5f"/>
              <circle cx="28" cy="155" r="3" fill="#a78bca"/>
              {/* Mid-left drift */}
              <circle cx="68" cy="168" r="5" fill="#a78bca"/>
              <circle cx="82" cy="172" r="7" fill="#d98b5f"/>
              <circle cx="72" cy="158" r="3" fill="#e8a547"/>
              {/* Centre-front sunflower hero (tiny callback to Beginner's sunflower) */}
              <circle cx="110" cy="170" r="8" fill="#f0c840"/>
              <circle cx="110" cy="170" r="3" fill="#7a5a3d"/>
              {/* Mid-right drift */}
              <circle cx="128" cy="168" r="4" fill="#a78bca"/>
              <circle cx="142" cy="172" r="6" fill="#c96878"/>
              <circle cx="155" cy="165" r="3" fill="#d98b5f"/>
              {/* Hero cluster bottom-right */}
              <circle cx="180" cy="172" r="9" fill="#a78bca"/>
              <circle cx="198" cy="168" r="5" fill="#c96878"/>
              <circle cx="208" cy="173" r="3" fill="#e8a547"/>
            </svg>
          </div>

          {/* D-09 — native range slider */}
          <input
            type="range"
            min="0"
            max="2"
            step="1"
            value={expIndex}
            onChange={handleExpChange}
            className="onb-experience-slider"
            aria-label="Experience level"
            aria-valuemin={0}
            aria-valuemax={2}
            aria-valuenow={expIndex}
            aria-valuetext={ONBOARD_EXPERIENCE[expIndex].label}
          />

          {/* D-10 — three tick labels */}
          <div className="onb-experience-ticks">
            {ONBOARD_EXPERIENCE.map((opt, i) => (
              <span key={opt.value} className={i === expIndex ? "is-active" : ""}>{opt.label}</span>
            ))}
          </div>
        </div>

        {/* Location — UNCHANGED logic, restyled to use .onb-pc classes */}
        <div style={{ marginTop: 20 }}>
          <label htmlFor="onb-location" className="onb-field-label">Where do you garden?</label>
          <div className="onb-pc">
            <div style={{ position: "relative", flex: 1 }}>
              <input
                id="onb-location"
                type="text"
                className={`onb-pc-input ${locStatus === "valid" ? "is-valid" : ""}`}
                value={data.location}
                onChange={e => setData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. SW1A"
                maxLength={8}
                aria-invalid={locStatus === "error"}
                aria-describedby="onb-location-hint"
              />
              {locStatus === "valid" && (
                <span aria-hidden="true" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--c-heading)", display: "flex" }}>
                  <Check size={16}/>
                </span>
              )}
            </div>
            <button
              type="button"
              className="onb-pc-geo"
              onClick={handleUseMyLocation}
              disabled={locStatus === "detecting"}
              aria-label="Use my location"
            >
              <MapPin size={14}/>
              {locStatus === "detecting" ? "Detecting…" : "Use my location"}
            </button>
          </div>
          <div id="onb-location-hint" className="onb-margin-note" style={{ color: locStatus === "error" ? "var(--c-danger)" : "var(--c-text-2)", marginTop: 6, minHeight: 18 }}>
            {locStatus === "error"
              ? locError
              : (locStatus === "valid"
                  ? "Looks good."
                  : "A plant in London follows a different schedule to one in Sydney. We only keep the first part of your postcode (like SW1A), and it stays on this device.")}
          </div>
        </div>

        <div className="push-bottom">
          <button type="button" className="onb-cta" onClick={handleContinue}>
            Next — your growing spaces →
          </button>
        </div>
      </div>
    </OnbFrame>
  );
}
